import * as createProducts from 'store/actions/products/createProducts';
import * as deleteProducts from 'store/actions/products/deleteProducts';
import * as fetchProducts from 'store/actions/products/fetchProducts';
import * as types from 'store/actions/types';
import * as updateProduct from 'store/actions/products/updateProducts';
import apiClient from 'clients/apiClient';
import config from 'helpers/config';
import utils from 'helpers/utils';
import { put, select, takeEvery } from 'redux-saga/effects';
import { showNotification } from 'store/actions/uiActions';

const getProductsStore = (state) => {
    return state.products;
};

function* fetchProductsAccumulatedAsync({ payload }) {
    const params = Object.assign({
        includes: [
            'category',
        ],
    }, payload.params || {
        size: payload.size,
        offset: payload.offset,
        sort_by: payload.sort.field,
        sort_direction: payload.sort.order,
    });

    try {
        const url = `${ config.getApiBaseUrl() }/products`;

        const response = yield apiClient.read(url, params);

        yield put(fetchProducts.fetchProductsSuccess(response));
    } catch (error) {
        yield put(fetchProducts.fetchProductsFailure(error));
    }
}

function* fetchProductsAsync({ payload }) {
    try {
        const url = `${ config.getApiBaseUrl() }/products`;
        const params = Object.assign({
            includes: [
                'category',
            ],
        }, payload || {});

        const response = yield apiClient.read(url, params);

        yield put(fetchProducts.fetchProductsSuccess(response));
    } catch (error) {
        yield put(fetchProducts.fetchProductsFailure(error));
    }
}

function* fetchProductAsync({ payload }) {
    const {
        id,
        onError,
        onSuccess,
        ...query
    } = payload;

    try {
        const url = `${ config.getApiBaseUrl() }/products/${ id }`;
        const params = Object.assign({
            includes: [
                'category',
            ],
        }, query || {});

        const response = yield apiClient.read(url, params);

        yield put(fetchProducts.fetchProductSuccess(response));

        if (utils.isFunction(onSuccess)) {
            yield onSuccess();
        }
    } catch (error) {
        yield put(fetchProducts.fetchProductFailure(error));

        if (utils.isFunction(onError)) {
            yield onError(error);
        }
    }
}

function* createProductAsync({ payload }) {
    const { values } = payload;

    try {
        const url = `${ config.getApiBaseUrl() }/products`;

        const response = yield apiClient.create(url, values);

        yield put(createProducts.createProductSuccess(response));
        payload.onSuccess(response);
    } catch (error) {
        yield put(createProducts.createProductFailure(error));
        yield payload.onError();
    }
}

function* createProductSuccessAsync() {
    yield put(showNotification('notifications.products.create.success'));
}

function* updateProductAsync({ payload }) {
    const {
        id,
        onError,
        onSuccess,
        values,
    } = payload;

    try {
        const url = `${ config.getApiBaseUrl() }/products/${ id }`;

        const response = yield apiClient.patch(url, values);

        yield put(updateProduct.updateProductSuccess(payload, response));

        yield onSuccess();
    } catch (error) {
        yield put(updateProduct.updateProductFailure(error));

        yield onError(error);
    }
}

function* updateProductSuccessAsync() {
    yield put(showNotification('notifications.products.update.success'));
}

function* updateProductActivityErrorAsync({ error }) {
    let message = error.response.message;

    if (message == 'product-missing-data-for-enable') {
        message = `resources.products.notifications.${ error.response.message }`;
    }

    yield put(showNotification(message, 'warning'));
}

function* updateProductLocationAsync({ payload }) {
    const {
        id,
        onError,
        onSuccess,
        values,
    } = payload;

    try {
        const url = `${ config.getApiBaseUrl() }/products/${ id }/location`;

        const response = yield apiClient.patch(url, values);

        yield put(updateProduct.updateProductLocationSuccess(payload, response));

        yield onSuccess();
    } catch (error) {
        yield put(updateProduct.updateProductLocationFailure(error));

        yield onError(error);
    }
}

function* updateProductLocationSuccessAsync() {
    yield put(showNotification('notifications.products.update.success'));
}

function* deleteProductAsync({ payload }) {
    try {
        const url = `${ config.getApiBaseUrl() }/products/${ payload.id }`;

        yield apiClient.delete(url);

        yield put(deleteProducts.deleteProductSuccess(payload));
    } catch (error) {
        yield put(deleteProducts.deleteProductFailure(error));
    }
}

function* deleteProductSuccessAsync() {
    const getProducts = yield select(getProductsStore);

    yield put(fetchProducts.fetchProducts({
        offset: getProducts.offset,
        page: getProducts.page,
    }));

    yield put(showNotification('notifications.products.delete.success', 'success'));
}

function* failureMessage({ error }) {
    const { message } = error.response;

    if (!message) {
        return;
    }

    yield put(showNotification(message, 'warning'));
}

function* createProductLogoAsync({ payload }) {
    const { id, formData } = payload;

    try {
        const url = `${ config.getApiBaseUrl() }/products/${ id }/logo`;

        const response = yield apiClient.createFile(url, formData);

        yield put(createProducts.createProductLogoSuccess(id, response));
    } catch (error) {
        yield put(createProducts.createProductLogoFailure(error));
    }
}

function* createProductLogoSuccessAsync() {
    yield put(showNotification('notifications.products.logo.create.success'));
}

function* createProductLogoFailureAsync({ error }) {
    yield put(showNotification(error.response.message, 'warning'));
}

function* deleteProductLogoSuccessAsync() {
    yield put(showNotification('notifications.products.logo.delete.success'));
}

function* deleteProductLogoFailureAsync() {
    yield put(showNotification('notifications.products.logo.delete.failure', 'warning'));
}

export default function* productsSagas() {
    yield [
        takeEvery(types.FETCH_PRODUCTS, fetchProductsAsync),
        takeEvery(types.FETCH_PRODUCTS_ACCUMULATED, fetchProductsAccumulatedAsync),
        takeEvery(types.FETCH_PRODUCTS_FAILURE, failureMessage),

        takeEvery(types.FETCH_PRODUCT, fetchProductAsync),
        takeEvery(types.FETCH_PRODUCT_FAILURE, failureMessage),

        takeEvery(types.CREATE_PRODUCT, createProductAsync),
        takeEvery(types.CREATE_PRODUCT_FAILURE, failureMessage),
        takeEvery(types.CREATE_PRODUCT_SUCCESS, createProductSuccessAsync),

        takeEvery(types.UPDATE_PRODUCT, updateProductAsync),
        takeEvery(types.UPDATE_PRODUCT_FAILURE, failureMessage),
        takeEvery(types.UPDATE_PRODUCT_SUCCESS, updateProductSuccessAsync),

        takeEvery(types.UPDATE_PRODUCT_LOCATION, updateProductLocationAsync),
        takeEvery(types.UPDATE_PRODUCT_LOCATION_FAILURE, failureMessage),
        takeEvery(types.UPDATE_PRODUCT_LOCATION_SUCCESS, updateProductLocationSuccessAsync),

        takeEvery(types.UPDATE_PRODUCT_ACTIVITY_FAILURE, updateProductActivityErrorAsync),

        takeEvery(types.DELETE_PRODUCT, deleteProductAsync),
        takeEvery(types.DELETE_PRODUCT_FAILURE, failureMessage),
        takeEvery(types.DELETE_PRODUCT_SUCCESS, deleteProductSuccessAsync),

        takeEvery(types.CREATE_PRODUCT_LOGO, createProductLogoAsync),
        takeEvery(types.CREATE_PRODUCT_LOGO_FAILURE, createProductLogoFailureAsync),
        takeEvery(types.CREATE_PRODUCT_LOGO_SUCCESS, createProductLogoSuccessAsync),

        takeEvery(types.DELETE_PRODUCT_LOGO_FAILURE, deleteProductLogoFailureAsync),
        takeEvery(types.DELETE_PRODUCT_LOGO_SUCCESS, deleteProductLogoSuccessAsync),
    ];
}



// WEBPACK FOOTER //
// ./src/store/sagas/productsSagas.js